import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, Paper } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PieChartIcon from '@mui/icons-material/PieChart';
import AssessmentIcon from '@mui/icons-material/Assessment';

const TEAL = '#0d9488';
const DARK_BG = '#052d50';
const GRADIENT = 'linear-gradient(135deg, #052d50 0%, #478289 100%)';

const valueProps = [
  { icon: <SearchIcon sx={{ fontSize: 40 }} />, title: 'Origination', desc: 'We source the best deals from Israel\'s top tech ecosystem, leveraging deep networks and proprietary deal flow.' },
  { icon: <TrendingUpIcon sx={{ fontSize: 40 }} />, title: 'Theme-based Investment', desc: 'Our investment themes focus on sectors with high growth potential — cybersecurity, fintech, health-tech, and more.' },
  { icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />, title: 'Portfolio Management', desc: 'Active portfolio management ensures your investments are monitored and optimized for maximum returns.' },
  { icon: <VisibilityIcon sx={{ fontSize: 40 }} />, title: 'Representation & Monitoring', desc: 'We represent our investors on company boards and provide regular updates on portfolio performance.' },
];

const howItWorks = [
  { icon: <PersonAddIcon sx={{ fontSize: 36 }} />, step: '01', title: 'Sign Up', desc: 'Create your account and complete our accreditation process.' },
  { icon: <LockOpenIcon sx={{ fontSize: 36 }} />, step: '02', title: 'Gain Access', desc: 'Browse exclusive investment opportunities in Israeli tech startups.' },
  { icon: <PieChartIcon sx={{ fontSize: 36 }} />, step: '03', title: 'Build Your Portfolio', desc: 'Invest in individual deals or our diversified fund.' },
  { icon: <AssessmentIcon sx={{ fontSize: 36 }} />, step: '04', title: 'Get Quarterly Updates', desc: 'Stay informed with detailed reports on your portfolio companies.' },
];

const exitLogos = ['Lightricks', 'Lemonade', 'Monday.com', 'ironSource', 'Fiverr', 'Payoneer'];

const Investors = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Helmet>
        <title>Investors — iAngels | Invest in Israeli Innovation</title>
        <meta name="description" content="Get insider access to Israel's best investment opportunities. Build a diversified portfolio of Israeli tech startups with iAngels." />
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: GRADIENT, color: 'white', py: { xs: 8, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            Your Gateway to Investing in Israeli Innovation
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
            Get insider access to Israel's best investment opportunities
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: TEAL, px: 5, py: 1.5, '&:hover': { bgcolor: '#0b7d73' } }}
          >
            Start Investing
          </Button>
        </Container>
      </Box>

      {/* Exits & Unicorns */}
      <Box sx={{ py: 6, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" align="center" display="block" sx={{ mb: 3, letterSpacing: 3, color: 'text.secondary' }}>
            Exits & Unicorns
          </Typography>
          <Grid container spacing={3} justifyContent="center" alignItems="center">
            {exitLogos.map((name) => (
              <Grid item xs={4} sm={2} key={name} sx={{ textAlign: 'center' }}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                  <Box sx={{ width: 48, height: 48, bgcolor: '#e0e0e0', borderRadius: '50%', mx: 'auto', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">{name}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Value Props */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>
            We Bring Value to Our Investors
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5, maxWidth: 600, mx: 'auto' }}>
            Our end-to-end approach covers every aspect of the investment lifecycle.
          </Typography>
          <Grid container spacing={4}>
            {valueProps.map((v) => (
              <Grid item xs={12} sm={6} md={3} key={v.title}>
                <Card elevation={0} sx={{ height: '100%', textAlign: 'center', p: 3, border: '1px solid #eee', borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ color: TEAL, mb: 2 }}>{v.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>{v.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{v.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 5, color: DARK_BG }}>
            How It Works
          </Typography>
          <Grid container spacing={4}>
            {howItWorks.map((h) => (
              <Grid item xs={12} sm={6} md={3} key={h.step}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ color: TEAL, fontWeight: 700, opacity: 0.3, mb: 1 }}>{h.step}</Typography>
                  <Box sx={{ color: TEAL, mb: 1 }}>{h.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>{h.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{h.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 8, background: GRADIENT, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
            Ready to Invest in Israeli Innovation?
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 4 }}>
            Join iAngels today and start building your portfolio.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: 'white', color: DARK_BG, px: 5, py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#e0e0e0' } }}
          >
            Sign Up Now
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Investors;
